import React, { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// Import dayjs locale files at module scope
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';

// Dynamic imports for antd locale
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';

interface AntdLocaleProviderProps {
  children: React.ReactNode;
}

const localeMap: Record<string, typeof zhCN> = {
  'zh-CN': zhCN,
  'en-US': enUS,
};

const dayjsLocaleMap: Record<string, string> = {
  'zh-CN': 'zh-cn',
  'en-US': 'en',
};

const AntdLocaleProvider: React.FC<AntdLocaleProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLocale, setCurrentLocale] = useState(() => {
    return localeMap[i18n.language] || zhCN;
  });

  useEffect(() => {
    // Set dayjs locale on mount
    const dayjsLoc = dayjsLocaleMap[i18n.language] || 'zh-cn';
    dayjs.locale(dayjsLoc);

    // Set antd locale
    setCurrentLocale(localeMap[i18n.language] || zhCN);

    // Listen for language changes
    const handleLanguageChanged = (lng: string) => {
      const dayjsLoc = dayjsLocaleMap[lng] || 'zh-cn';
      dayjs.locale(dayjsLoc);
      setCurrentLocale(localeMap[lng] || zhCN);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  return (
    <ConfigProvider locale={currentLocale}>
      {children}
    </ConfigProvider>
  );
};

export default AntdLocaleProvider;
